import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Center,
  Divider,
  useToast,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { formatDateForDisplay } from '../utils/dateUtils';

// FFM Brand Colors
const FFM_COLORS = {
  primary: 'rgb(78, 76, 134)',
  primaryLight: 'rgb(237, 237, 243)',
  accent: 'rgb(206, 16, 33)',
  text: 'rgb(51, 51, 51)',
  textLight: 'rgb(103, 109, 114)',
};

// Category options (matching original form)
const CATEGORIES = [
  'FFM',
  'Institutionnel',
  'Pilote',
  'Presse',
  'Promoteur / Organisateur / Circuit',
  'Univers Moto',
  'Pilote',
  'Divers',
];

interface RSVPFormData {
  // Step 1
  response_status: 'accepted' | 'declined' | '';
  
  // Common fields (both paths)
  email: string;
  phone: string;
  organization: string;
  
  // Attending only
  category?: string;
  position?: string;
}

interface EventData {
  event_title: string;
  event_date: string;
  event_location?: string;
  invitee_name: string;
  invitee_organization?: string;
  current_response_status: string;
  can_change_response: boolean;
  response_deadline?: string;
  header_image_url?: string;
}

// The event's own header (same image as its emails). If it fails to load, hide it: the page still works.
const EventHeaderImage: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      style={{
        width: '100%',
        height: 'auto',  // Preserves aspect ratio
        display: 'block',  // Removes extra space below image
      }}
    />
  );
};


console.log('🎯 RSVPPage.tsx loaded');

const RSVPPage: React.FC = () => {
  console.log('🎯 RSVPPage component rendering');
  const { token } = useParams<{ token: string }>();
  console.log('🎯 Token from URL:', token);
  const toast = useToast();

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<RSVPFormData>({
    response_status: '',
    email: '',
    phone: '',
    organization: '',
    category: '',
    position: '',
  });

  // Fetch RSVP form data
  useEffect(() => {
    const fetchRSVPData = async () => {
      if (!token) {
        toast({
          title: 'Erreur',
          description: 'Token d\'invitation manquant',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      try {
        setLoading(true);
        const data = await apiService.get<EventData>(`/invitees/rsvp/${token}`);
        setEventData(data);

        // Pre-fill existing response if any
        if (data.current_response_status !== 'pending') {
          setFormData(prev => ({
            ...prev,
            response_status: data.current_response_status as 'accepted' | 'declined',
          }));
        }
      } catch (error: any) {
        console.error('Error fetching RSVP data:', error);
        toast({
          title: 'Erreur',
          description: error.message || 'Invitation non trouvée ou expirée',
          status: 'error',
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRSVPData();
  }, [token, toast]);

  // Format date
// Update the formatDate function (around line 117)
  const formatDate = (dateString: string) => {
    return formatDateForDisplay(dateString, undefined, true);
  };

  // Handle form field changes
  const handleChange = (field: keyof RSVPFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate step
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.response_status) {
        newErrors.response_status = 'Veuillez sélectionner une réponse';
      }
    }

    if (step === 2) {
      // Common required fields
      if (!formData.email.trim()) {
        newErrors.email = 'L\'email est requis';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Format d\'email invalide';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Le numéro de téléphone est requis';
      }
      if (!formData.organization.trim()) {
        newErrors.organization = 'Le nom de l\'entité est requis';
      }

      // Attending-specific required fields
      if (formData.response_status === 'accepted') {
        if (!formData.category) {
          newErrors.category = 'La catégorie est requise';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle back
  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep(2) || !token) return;

    try {
      setSubmitting(true);


      // Prepare submission data
      const submissionData = {
        response_status: formData.response_status,
        
        // Respondent information
        respondent_email: formData.email,
        respondent_phone: formData.phone,
        respondent_organization: formData.organization,
        respondent_category: formData.category || '',
        job_title: formData.position || '',

        // Other fields
        company: formData.organization,
        dietary_restrictions: '',
        special_requests: '',
        number_of_guests: 0,
        plus_one_name: '',
        emergency_contact: '',
        emergency_phone: formData.phone,
      };

      await apiService.post(`/invitees/rsvp/${token}`, submissionData);

      setSubmitted(true);
      toast({
        title: 'Réponse enregistrée',
        description: 'Votre réponse a été enregistrée avec succès',
        status: 'success',
        duration: 5000,
      });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      console.error('Error submitting RSVP:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors de l\'enregistrement',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Center minH="100vh" bg="gray.50">
        <VStack spacing={4}>
          <Spinner size="xl" color={FFM_COLORS.primary} thickness="4px" />
          <Text color={FFM_COLORS.textLight}>Chargement...</Text>
        </VStack>
      </Center>
    );
  }

  // Error state (no event data)
  if (!eventData) {
    return (
      <Center minH="100vh" bg="gray.50">
        <Container maxW="container.md">
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="md"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Invitation non trouvée
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              Cette invitation n'existe pas ou a expiré.
            </AlertDescription>
          </Alert>
        </Container>
      </Center>
    );
  }

  // Success state
  if (submitted) {
    return (
      <Box minH="100vh" bg="gray.50">
        {/* Header Image - Using img tag to preserve aspect ratio */}
        <Box 
          display="flex" 
          justifyContent="center" 
          bg="gray.50"
          pt={0}
          px={4}
        >
          <Box
            maxW="container.md"
            w="full"
            borderRadius="0 0 16px 16px"
            overflow="hidden"  // Ensures border radius works
            boxShadow="lg"
          >
            <EventHeaderImage
              src={eventData?.header_image_url}
              alt={eventData?.event_title || ''}
            />
          </Box>
        </Box>

        <Container maxW="container.md" py={8}>
          <Card>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Center>
                  <Box
                    w="60px"
                    h="60px"
                    borderRadius="full"
                    bg="green.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="3xl">✓</Text>
                  </Box>
                </Center>

                <VStack spacing={2}>
                  <Heading size="lg" color={FFM_COLORS.primary} textAlign="center">
                    Réponse enregistrée
                  </Heading>
                  <Text color={FFM_COLORS.textLight} textAlign="center">
                    Votre réponse a été enregistrée avec succès
                  </Text>
                </VStack>

                <Divider />

                <VStack spacing={3} align="start">
                  <HStack>
                    <Text fontWeight="bold">Événement :</Text>
                    <Text>{eventData.event_title}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="bold">Date :</Text>
                    <Text>{formatDate(eventData.event_date)}</Text>
                  </HStack>
                  <HStack>
                    <Text fontWeight="bold">Votre réponse :</Text>
                    <Text
                      color={formData.response_status === 'accepted' ? 'green.600' : 'orange.600'}
                      fontWeight="bold"
                    >
                      {formData.response_status === 'accepted' ? 'Présent' : 'Absent'}
                    </Text>
                  </HStack>
                </VStack>

                {formData.response_status === 'accepted' && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize="sm">Confirmation</AlertTitle>
                      <AlertDescription fontSize="sm">
                        Un email de confirmation vous a été envoyé.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                {formData.response_status === 'declined' && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize="sm">Communiqué de presse</AlertTitle>
                      <AlertDescription fontSize="sm">
                        Vous recevrez le communiqué de presse par email.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                {eventData.can_change_response && (
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    size="sm"
                    onClick={() => {
                      setSubmitted(false);
                      setCurrentStep(1);
                    }}
                  >
                    Modifier ma réponse
                  </Button>
                )}
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  // Render form
  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header Image - Using img tag to preserve aspect ratio */}
      <Box 
        display="flex" 
        justifyContent="center" 
        bg="gray.50"
        pt={0}
        px={4}
      >
        <Box
          maxW="container.md"
          w="full"
          borderRadius="0 0 16px 16px"
          overflow="hidden"  // Ensures border radius works
          boxShadow="lg"
        >
          <EventHeaderImage
            src={eventData?.header_image_url}
            alt={eventData?.event_title || ''}
          />
        </Box>
      </Box>

      <Container maxW="container.md" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Event Details Card */}
          <Card bg="white">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="lg" color={FFM_COLORS.primary}>
                  {eventData.event_title}
                </Heading>

                <Divider />

                <VStack spacing={2} align="start">
                  <HStack align="start">
                    <Text fontWeight="bold" color={FFM_COLORS.text}>📅</Text>
                    <VStack align="start" spacing={1}>
                      <Text color={FFM_COLORS.textLight}>
                        {formatDate(eventData.event_date)}
                      </Text>
                    </VStack>
                  </HStack>

                  {eventData.event_location && (
                    <HStack align="start">
                      <Text fontWeight="bold" color={FFM_COLORS.text}>📍</Text>
                      <Text color={FFM_COLORS.textLight}>
                        {eventData.event_location}
                      </Text>
                    </HStack>
                  )}
                </VStack>

                {eventData.response_deadline && !eventData.can_change_response && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">
                      La date limite de réponse est dépassée
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Form Card */}
          <Card bg="white">
            <CardBody>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6} align="stretch">
                  {/* Progress indicator */}
                  <HStack spacing={2} justify="center">
                    <Box
                      w="30px"
                      h="30px"
                      borderRadius="full"
                      bg={currentStep >= 1 ? FFM_COLORS.primary : 'gray.200'}
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      1
                    </Box>
                    <Box h="2px" w="50px" bg={currentStep >= 2 ? FFM_COLORS.primary : 'gray.200'} />
                    <Box
                      w="30px"
                      h="30px"
                      borderRadius="full"
                      bg={currentStep >= 2 ? FFM_COLORS.primary : 'gray.200'}
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      2
                    </Box>
                  </HStack>

                  {/* Step 1: Response Choice */}
                  {currentStep === 1 && (
                    <VStack spacing={4} align="stretch">
                      <Heading size="md" color={FFM_COLORS.text}>
                        Serez-vous présent à la conférence de presse FFM ?
                      </Heading>

                      <FormControl isInvalid={!!errors.response_status} isRequired>
                        <RadioGroup
                          value={formData.response_status}
                          onChange={(value) => handleChange('response_status', value)}
                        >
                          <Stack spacing={3}>
                            <Radio
                              value="accepted"
                              colorScheme="green"
                              size="lg"
                              _checked={{
                                bg: FFM_COLORS.primary,
                                borderColor: FFM_COLORS.primary,
                              }}
                            >
                              <Text fontSize="md">Oui</Text>
                            </Radio>
                            <Radio
                              value="declined"
                              colorScheme="orange"
                              size="lg"
                              _checked={{
                                bg: FFM_COLORS.primary,
                                borderColor: FFM_COLORS.primary,
                              }}
                            >
                              <Text fontSize="md">Non</Text>
                            </Radio>
                          </Stack>
                        </RadioGroup>
                        {errors.response_status && (
                          <FormErrorMessage>{errors.response_status}</FormErrorMessage>
                        )}
                      </FormControl>

                      <Button
                        bg={FFM_COLORS.accent}
                        color="white"
                        size="lg"
                        width="full"
                        onClick={handleNext}
                        _hover={{ bg: 'rgb(185, 14, 30)' }}
                        isDisabled={!formData.response_status}
                      >
                        Suivant
                      </Button>
                    </VStack>
                  )}

                  {/* Step 2: Information Form */}
                  {currentStep === 2 && (
                    <VStack spacing={4} align="stretch">
                      <Heading size="md" color={FFM_COLORS.text}>
                        Informations personnelles
                      </Heading>

                      {formData.response_status === 'accepted' ? (
                        <Text fontSize="sm" color={FFM_COLORS.textLight}>
                          Nous vous prions de bien vouloir compléter les informations suivantes 
                          afin de vous garantir le meilleur accueil possible.
                        </Text>
                      ) : (
                        <Text fontSize="sm" color={FFM_COLORS.textLight}>
                          Si vous ne pouvez pas être présent, recevez par retour de ce formulaire 
                          le communiqué de presse relatif à notre conférence de presse.
                        </Text>
                      )}

                      {/* Common fields */}
                      <Box data-testid="rsvp-invitee-name">
                        <Text fontWeight="semibold" fontSize="lg" color={FFM_COLORS.text}>
                          {eventData?.invitee_name}
                        </Text>
                        <Text fontSize="sm" color={FFM_COLORS.textLight}>
                          Cette invitation est nominative. Si ce n'est pas vous, merci de contacter l'organisateur.
                        </Text>
                      </Box>

                      <FormControl isRequired isInvalid={!!errors.email}>
                        <FormLabel>E-mail</FormLabel>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="votre@email.com"
                        />
                        {errors.email && (
                          <FormErrorMessage>{errors.email}</FormErrorMessage>
                        )}
                      </FormControl>

                      <FormControl isRequired isInvalid={!!errors.phone}>
                        <FormLabel>N° Téléphone</FormLabel>
                        <Input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="06 12 34 56 78"
                        />
                        {errors.phone && (
                          <FormErrorMessage>{errors.phone}</FormErrorMessage>
                        )}
                      </FormControl>

                      {/* Attending-specific fields */}
                      {formData.response_status === 'accepted' && (
                        <>
                          <FormControl isRequired isInvalid={!!errors.category}>
                            <FormLabel>Catégorie</FormLabel>
                            <Select
                              value={formData.category}
                              onChange={(e) => handleChange('category', e.target.value)}
                              placeholder="Sélectionnez une catégorie"
                            >
                              {CATEGORIES.map((cat) => (
                                <option key={cat} value={cat}>
                                  {cat}
                                </option>
                              ))}
                            </Select>
                            {errors.category && (
                              <FormErrorMessage>{errors.category}</FormErrorMessage>
                            )}
                          </FormControl>

                          <FormControl isRequired isInvalid={!!errors.organization}>
                            <FormLabel>Nom de l'entité</FormLabel>
                            <Input
                              value={formData.organization}
                              onChange={(e) => handleChange('organization', e.target.value)}
                              placeholder="Nom de votre organisation"
                            />
                            {errors.organization && (
                              <FormErrorMessage>{errors.organization}</FormErrorMessage>
                            )}
                          </FormControl>

                          <FormControl>
                            <FormLabel>Fonction</FormLabel>
                            <Input
                              value={formData.position}
                              onChange={(e) => handleChange('position', e.target.value)}
                              placeholder="Votre fonction (optionnel)"
                            />
                          </FormControl>
                        </>
                      )}

                      {/* Not attending - only entity name */}
                      {formData.response_status === 'declined' && (
                        <FormControl isRequired isInvalid={!!errors.organization}>
                          <FormLabel>Nom de l'entité</FormLabel>
                          <Input
                            value={formData.organization}
                            onChange={(e) => handleChange('organization', e.target.value)}
                            placeholder="Nom de votre organisation"
                          />
                          {errors.organization && (
                            <FormErrorMessage>{errors.organization}</FormErrorMessage>
                          )}
                        </FormControl>
                      )}

                      {/* Action buttons */}
                      <HStack spacing={4} pt={4}>
                        <Button
                          variant="outline"
                          onClick={handleBack}
                          isDisabled={submitting}
                          flex={1}
                        >
                          Retour
                        </Button>
                        <Button
                          type="submit"
                          bg={FFM_COLORS.accent}
                          color="white"
                          size="lg"
                          flex={2}
                          isLoading={submitting}
                          loadingText="Envoi..."
                          _hover={{ bg: 'rgb(185, 14, 30)' }}
                        >
                          Envoyer
                        </Button>
                      </HStack>

                      <Text fontSize="xs" color={FFM_COLORS.textLight} textAlign="center">
                        N'envoyez jamais de mots de passe via ce formulaire
                      </Text>
                    </VStack>
                  )}
                </VStack>
              </form>
            </CardBody>
          </Card>

          {/* Footer */}
          <Text fontSize="xs" color={FFM_COLORS.textLight} textAlign="center">
            Ce formulaire a été créé par la Fédération Française de Motocyclisme
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default RSVPPage;